// SignerHeader.tsx
import React, { useRef, useState } from "react";
import {
  Stack,
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import InfoHelper from "global/components/InfoHelperText/InfoHelper";
import { useFeatureState } from "hooks/use-feature";
import FeatureKeys from "global/constants/FeatureKeys";
import strings from "global/constants/StringConstants";
import approversAndSignersStyles from "../ApproversAndSigners.styles";
import recipientsTokens from "../ApproversAndSigners.tokens";
import { tourAttr } from "tour";
import {
  CaretDownGlyph,
  InfoCircleGlyph,
  PeopleGlyph,
  PersonGlyph,
  PlusGlyph,
  TickGlyph,
} from "./RecipientsIcons";

const styles = approversAndSignersStyles;
const { addButton } = recipientsTokens;

interface SignerHeaderProps {
  tabValue: string;
  setTabValue: React.Dispatch<React.SetStateAction<"signer" | "approver">>;
  signers: any[];
  isBulkSend: boolean;
  signerOrderRequired: boolean;
  approvalOrderNeeded: boolean;
  reviewers: any[];
  isMobile: boolean;
  onSignerOrderChange: (checked: boolean) => void;
  onApproverOrderChange: (checked: boolean) => void;
  onAddSigner: () => void;
  onAddApprover: () => void;
  /** Adds a copy-only recipient — the second entry of the design's add menu. */
  onAddCc?: () => void;
  /** Adds an approver group — the second entry of the approver add menu. */
  onAddGroup?: () => void;
}

/** One entry of the split-button dropdown: bold title over a grey caption. */
interface AddOption {
  title: string;
  caption: string;
  onClick?: () => void;
}

const SignerHeader: React.FC<SignerHeaderProps> = (props) => {
  const {
    tabValue,
    setTabValue,
    signers,
    isBulkSend,
    signerOrderRequired,
    approvalOrderNeeded,
    reviewers,
    isMobile,
    onSignerOrderChange,
    onApproverOrderChange,
    onAddSigner,
    onAddApprover,
    onAddCc,
    onAddGroup,
  } = props;

  const onSigners = tabValue === "signer";
  const [addAnchorEl, setAddAnchorEl] = useState<null | HTMLElement>(null);
  const addGroupRef = useRef<HTMLDivElement | null>(null);

  const signingOrderState = useFeatureState(FeatureKeys.ENVELOPE_SIGNING_ORDER);
  const isSigningOrderAvailable =
    signingOrderState.status === strings.FEATURE_STATUS_AVAILABLE;
  const signingOrderDisabledTooltip =
    signingOrderState.status === strings.FEATURE_STATUS_ADMIN_DISABLED
      ? ((signingOrderState as any).disabledMessage ??
        strings.FEATURE_GATE_TEMPORARILY_UNAVAILABLE_TOOLTIP)
      : signingOrderState.status === strings.FEATURE_STATUS_NOT_IN_PLAN
        ? strings.FEATURE_GATE_UPSELL_TOOLTIP
        : "";

  const approvalWorkflowsState = useFeatureState(
    FeatureKeys.TEMPLATE_APPROVAL_WORKFLOWS,
  );
  const isApprovalWorkflowsAvailable =
    approvalWorkflowsState.status === strings.FEATURE_STATUS_AVAILABLE;
  const approvalWorkflowsTooltip =
    approvalWorkflowsState.status === strings.FEATURE_STATUS_ADMIN_DISABLED
      ? ((approvalWorkflowsState as any).disabledMessage ??
        strings.FEATURE_GATE_TEMPORARILY_UNAVAILABLE_TOOLTIP)
      : approvalWorkflowsState.status === strings.FEATURE_STATUS_NOT_IN_PLAN
        ? strings.FEATURE_GATE_UPSELL_TOOLTIP
        : "";

  const orderChecked = onSigners
    ? isSigningOrderAvailable && signerOrderRequired
    : isApprovalWorkflowsAvailable && approvalOrderNeeded;
  const orderDisabled = onSigners
    ? !isSigningOrderAvailable
    : !isApprovalWorkflowsAvailable;
  const orderTooltip = onSigners
    ? signingOrderDisabledTooltip
    : approvalWorkflowsTooltip;
  const showOrderToggle = onSigners
    ? signers?.length > 0 && !isBulkSend
    : reviewers?.length > 0;

  const addOptions: AddOption[] = onSigners
    ? [
        {
          title: "Signer",
          caption: "Must sign the document",
          onClick: onAddSigner,
        },
        {
          title: "CC recipient",
          caption: "Only receives the final document",
          onClick: onAddCc,
        },
      ]
    : [
        {
          title: "Single approver",
          caption: "One person approves this step",
          onClick: onAddApprover,
        },
        {
          title: "Approver group",
          caption: "A team — any one or everyone approves",
          onClick: onAddGroup,
        },
      ];
  const availableAddOptions = addOptions.filter((option) => option.onClick);

  const renderOrderToggle = () => {
    if (!showOrderToggle) return null;

    return (
      <Tooltip
        describeChild
        title={orderDisabled ? orderTooltip : ""}
        arrow
        disableHoverListener={!orderDisabled}
        disableFocusListener={!orderDisabled}
        disableTouchListener={!orderDisabled}
      >
        <Box component="span" sx={{ display: "inline-flex" }}>
          <Button
            id="add-signers-order-checkbox"
            disableRipple
            disabled={orderDisabled}
            role="checkbox"
            aria-checked={orderChecked}
            sx={styles.orderToggle}
            onClick={() =>
              onSigners
                ? onSignerOrderChange(!signerOrderRequired)
                : onApproverOrderChange(!approvalOrderNeeded)
            }
          >
            <Box sx={styles.orderBox(orderChecked)}>
              <TickGlyph sx={styles.orderTick(orderChecked)} />
            </Box>
            <Box component="span" sx={styles.orderLabel}>
              {onSigners ? "Set Signer's order" : "Set approval order"}
            </Box>
          </Button>
        </Box>
      </Tooltip>
    );
  };

  /**
   * The design draws a bare circle-i with a native tooltip; `InfoHelper` keeps
   * the app's richer explanation, so it wraps the design's glyph.
   */
  const renderOrderInfo = () => {
    if (!showOrderToggle) return null;

    return (
      <>
        <InfoHelper
          title={onSigners ? "Set Signer's Order" : "Set Approvers' Order"}
          modal={isMobile}
          description={
            onSigners
              ? "Signing runs level by level. Signers in the same level are notified together (parallel); levels run one after another."
              : "When on, approvers act one after another in the order shown. When off, all approvers are asked at once."
          }
          icon={<InfoCircleGlyph sx={styles.orderInfoIcon} />}
          buttonSx={styles.orderInfoButton}
        />
        {orderDisabled && (
          <LockOutlinedIcon sx={{ fontSize: 14, color: "text.disabled" }} />
        )}
      </>
    );
  };

  const renderTab = (
    value: "signer" | "approver",
    label: string,
    count: number,
    Icon: typeof PersonGlyph,
    disabled = false,
  ) => (
    <Button
      disableRipple
      disabled={disabled}
      aria-pressed={tabValue === value}
      onClick={() => setTabValue(value)}
      sx={styles.tab(tabValue === value)}
    >
      <Icon sx={styles.tabIcon} />
      {label}
      <Box component="span" sx={styles.tabCount}>
        {count}
      </Box>
      {disabled && (
        <LockOutlinedIcon sx={{ fontSize: 14, color: "text.disabled" }} />
      )}
    </Button>
  );

  const renderTabs = () => (
    <Box {...tourAttr("request.recipientTabs")} sx={styles.tabs}>
      {renderTab("signer", "Signers", signers?.length ?? 0, PersonGlyph)}
      {/* In the embedded wizard too: approvers approve inside the partner's app (EMBEDDED),
          like signers; Inkfree emails them nothing. */}
      <Tooltip
        describeChild
        title={!isApprovalWorkflowsAvailable ? approvalWorkflowsTooltip : ""}
        arrow
        disableHoverListener={isApprovalWorkflowsAvailable}
        disableFocusListener={isApprovalWorkflowsAvailable}
        disableTouchListener={isApprovalWorkflowsAvailable}
      >
        <Box component="span" sx={{ display: "inline-flex" }}>
          {renderTab(
            "approver",
            "Approvers",
            reviewers?.length ?? 0,
            PeopleGlyph,
            !isApprovalWorkflowsAvailable,
          )}
        </Box>
      </Tooltip>
    </Box>
  );

  const renderAddButton = () => {
    const disabled = !onSigners && !isApprovalWorkflowsAvailable;

    return (
      <Box sx={styles.toolbarActions}>
        <Box
          ref={addGroupRef}
          {...tourAttr("request.recipientsAdd")}
          sx={styles.addGroup}
        >
          <Button
            disableRipple
            disabled={disabled}
            onClick={onSigners ? onAddSigner : onAddApprover}
            sx={styles.addMain}
          >
            <PlusGlyph sx={styles.addIcon} />
            {onSigners ? "Add signer" : "Add approver"}
          </Button>
          {availableAddOptions.length > 1 && (
            <>
              <Box sx={styles.addDivider} />
              <IconButton
                disableRipple
                disabled={disabled}
                aria-label={onSigners ? "Add signer options" : "Add approver options"}
                onClick={() => setAddAnchorEl(addGroupRef.current)}
                sx={styles.addCaret}
              >
                <CaretDownGlyph sx={styles.addCaretIcon} />
              </IconButton>
            </>
          )}
        </Box>

        <Menu
          anchorEl={addAnchorEl}
          open={Boolean(addAnchorEl)}
          onClose={() => setAddAnchorEl(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          MenuListProps={{ sx: { p: 0 } }}
          PaperProps={{
            sx: styles.menuPaper(
              onSigners
                ? addButton.menuWidthSigner
                : addButton.menuWidthApprover,
            ),
          }}
        >
          {availableAddOptions.map((option) => (
            <MenuItem
              key={option.title}
              onClick={() => {
                option.onClick?.();
                setAddAnchorEl(null);
              }}
              sx={styles.menuItem}
            >
              <Typography component="div" sx={styles.menuItemTitle}>
                {option.title}
              </Typography>
              <Typography component="div" sx={styles.menuItemSub}>
                {option.caption}
              </Typography>
            </MenuItem>
          ))}
        </Menu>
      </Box>
    );
  };

  const renderMobile = () => (
    <Stack direction="column" spacing={1}>
      <Box sx={{ display: "flex", justifyContent: "center" }}>
        {renderTabs()}
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          "&.MuiBox-root": { marginTop: 2 },
        }}
      >
        <Box sx={styles.toolbarLead}>
          {renderOrderToggle()}
          {renderOrderInfo()}
        </Box>
        {renderAddButton()}
      </Box>
    </Stack>
  );

  const renderDesktop = () => (
    <Box sx={styles.toolbar}>
      <Box sx={styles.toolbarLead}>
        {renderOrderToggle()}
        {renderOrderInfo()}
      </Box>
      {renderTabs()}
      {renderAddButton()}
    </Box>
  );

  return isMobile ? renderMobile() : renderDesktop();
};

export default SignerHeader;
